"use client";

import Link from "next/link";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { pageVariants } from "@/lib/animations";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { MenuConstants } from "@/constants/menu";
import { sanitizeCmsHtml } from "@/lib/sanitize-cms-html";

/** Headless UI Menu açıkken indicator'ı doğru öğede göstermek için */
function SyncDropdownOpen({ open, subIndex, onSync }: { open: boolean; subIndex: number; onSync: (index: number | null) => void }) {
    useEffect(() => {
        onSync(open ? subIndex : null);
    }, [open, subIndex, onSync]);
    return null;
}

/** Alt menü öğesi (dropdown içinde, tıklanınca ilgili bölüme scroll) */
export interface MenuItemChild {
    title: string;
    link: string;
}

export interface MenuItems {
    title: string;
    link: string;
    svg: string;
    iconType?: 'svg' | 'image'; // Icon tipi (SVG content veya image URL)
    iconMediaId?: number | null; // Opsiyonel: Media ID
    isTab?: boolean; // Tab olarak mı gösterilecek? (scrollable section değil)
    /** false ise sekme başlığı (menüde ve sayfada) gizlenir. Varsayılan true. */
    showSectionTitle?: boolean;
    /** true ise bu öğe dropdown olarak gösterilir; children ile alt başlıklar listelenir. */
    isDropdown?: boolean;
    /** Dropdown alt öğeleri (çoklu dil başlıkları – sayfada ilgili alana scroll) */
    children?: MenuItemChild[];
    /** Özel aksiyon: tıklanınca rezervasyon modalı açılır (scroll yok) */
    actionType?: 'open_reservation_modal';
    /** Menü öğesi için hover tooltip metni */
    tooltip?: string;
}

interface ProductDetailMenuProps {
    /**
     * Menü öğeleri; sayfa üzerindeki `#id` bölümleriyle **aynı sırada** olmalı (ör. ProductTourDetail `displayMenuItems` içinde `contentOrderSections`’a göre sıralanır).
     */
    links?: MenuItems[];
    /** Book now gibi actionType kullanıldığında çağrılır */
    onOpenReservationModal?: () => void;
}

export default function ProductDetailMenu({ links: propLinks, onOpenReservationModal }: ProductDetailMenuProps = {}) {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
    
    const x = useMotionValue(0);
    const width = useMotionValue(0);
    const springConfig = { 
        stiffness: MenuConstants.SPRING_STIFFNESS, 
        damping: MenuConstants.SPRING_DAMPING 
    };
    const xSpring = useSpring(x, springConfig);
    const widthSpring = useSpring(width, springConfig);

    const links = propLinks || [];
    const [visibleLinks, setVisibleLinks] = useState<MenuItems[]>(links);

    useEffect(() => {
        if (typeof window === 'undefined') {
            setVisibleLinks(links);
            return;
        }

        const hasRenderableSection = (rawLink: string): boolean => {
            if (!rawLink || !rawLink.startsWith('#')) return true;
            const id = rawLink.replace(/^#/, '');
            if (!id) return false;
            const element = document.getElementById(id);
            if (!element) return false;

            // Fiyat bölümü lazy yüklenebilir; #pricing DOM'da varken menü linkini gizleme
            if (id === 'pricing') {
                return true;
            }

            const textSize = (element.textContent || '').replace(/\s+/g, '').length;
            const hasMeaningfulNode = element.querySelector('img,video,svg,canvas,iframe,input,button,table,ul,ol,li,[data-has-content="true"]') !== null;
            return textSize > 0 || hasMeaningfulNode;
        };

        const computeVisibleLinks = () => {
            const next = links.reduce<MenuItems[]>((acc, item) => {
                if (item.actionType === 'open_reservation_modal') {
                    acc.push(item);
                    return acc;
                }

                if (item.isDropdown && item.children?.length) {
                    const filteredChildren = item.children.filter((child) => hasRenderableSection(child.link));
                    if (filteredChildren.length > 0) {
                        acc.push({ ...item, children: filteredChildren });
                    }
                    return acc;
                }

                if (hasRenderableSection(item.link)) {
                    acc.push(item);
                }

                return acc;
            }, []);

            setVisibleLinks((prev) => {
                const prevSig = JSON.stringify(prev);
                const nextSig = JSON.stringify(next);
                return prevSig === nextSig ? prev : next;
            });
        };

        computeVisibleLinks();

        const observer = new MutationObserver(() => {
            computeVisibleLinks();
        });
        observer.observe(document.body, { childList: true, subtree: true });
        window.addEventListener('load', computeVisibleLinks);
        window.addEventListener('hashchange', computeVisibleLinks);
        window.addEventListener('resize', computeVisibleLinks);

        return () => {
            observer.disconnect();
            window.removeEventListener('load', computeVisibleLinks);
            window.removeEventListener('hashchange', computeVisibleLinks);
            window.removeEventListener('resize', computeVisibleLinks);
        };
    }, [links]);

    /** Her bölüm linki (hash) için menü öğe indeksi. Dropdown child'ları parent indeksini kullanır. */
    const linkToIndexMap = useCallback((): Map<string, number> => {
        const map = new Map<string, number>();
        visibleLinks.forEach((item, index) => {
            if (item.isDropdown && item.children?.length) {
                item.children.forEach((child) => {
                    const hash = child.link.startsWith('#') ? child.link : `#${child.link}`;
                    map.set(hash, index);
                    map.set(hash.substring(1), index);
                });
            } else {
                const hash = item.link.startsWith('#') ? item.link : `#${item.link}`;
                map.set(hash, index);
                map.set(hash.substring(1), index);
            }
        });
        return map;
    }, [visibleLinks]);

    /** Scroll/hash ile kullanılacak tüm bölüm seçicileri ve indeksleri */
    const sectionsWithIndex = useCallback((): Array<{ selector: string; index: number }> => {
        const result: Array<{ selector: string; index: number }> = [];
        visibleLinks.forEach((item, index) => {
            if (item.isDropdown && item.children?.length) {
                item.children.forEach((child) => {
                    const id = child.link.replace(/^#/, '');
                    if (id) result.push({ selector: `#${id}`, index });
                });
            } else {
                const id = item.link.replace(/^#/, '');
                if (id) result.push({ selector: `#${id}`, index });
            }
        });
        return result;
    }, [visibleLinks]);

    const updateIndicator = useCallback((index: number) => {
        const item = itemRefs.current[index];
        const container = menuRef.current;
        if (item && container) {
            const menuRect = container.getBoundingClientRect();
            const itemRect = item.getBoundingClientRect();
            x.set(itemRect.left - menuRect.left);
            width.set(itemRect.width);
        }
    }, [x, width]);

    useEffect(() => {
        const map = linkToIndexMap();
        const handleHashScroll = () => {
            const hash = window.location.hash;
            if (hash) {
                const index = map.get(hash) ?? visibleLinks.findIndex(l => l.link === hash);
                if (index !== -1) {
                    const id = hash.replace(/^#/, '');
                    const element = document.getElementById(id);
                    if (element && menuRef.current) {
                        const menuHeight = menuRef.current.offsetHeight || MenuConstants.DEFAULT_MENU_HEIGHT;
                        const offset = MenuConstants.calculateTotalOffset(menuHeight);
                        const elementPosition = element.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - offset;
                        
                        setActiveIndex(index);
                        updateIndicator(index);
                        
                        window.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                        });
                        return true;
                    }
                }
            }
            return false;
        };

        if (handleHashScroll()) {
            window.addEventListener('load', () => {
                setTimeout(handleHashScroll, MenuConstants.HASH_SCROLL_DELAY);
            });
        } else {
            setActiveIndex(0);
            updateIndicator(0);
        }
    }, [visibleLinks, linkToIndexMap, updateIndicator]);

    const prevActiveIndexRef = useRef<number | null>(null);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        const sections = sectionsWithIndex();
        const pagePath = window.location.pathname;
        const handleScroll = () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }

            rafRef.current = requestAnimationFrame(() => {
                if (sections.length === 0 || window.location.pathname !== pagePath || !menuRef.current?.isConnected) return;

                const menuHeight = menuRef.current?.offsetHeight || MenuConstants.DEFAULT_MENU_HEIGHT;
                const threshold = MenuConstants.calculateThreshold(menuHeight);
                let activeFound = false;
                let newActiveIndex: number | null = null;
                let newHash: string | null = null;

                for (let i = sections.length - 1; i >= 0; i--) {
                    const { selector, index } = sections[i];
                    const element = document.querySelector(selector);
                    if (element) {
                        const rect = element.getBoundingClientRect();
                        if (rect.top <= threshold) {
                            newActiveIndex = index;
                            newHash = selector;
                            activeFound = true;
                            break;
                        }
                    }
                }

                if (activeFound && newHash && window.location.hash !== newHash) {
                    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${newHash}`);
                }

                if (!activeFound && window.scrollY < threshold) {
                    newActiveIndex = 0;
                    if (window.location.hash) {
                        window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
                    }
                }

                if (newActiveIndex !== null && newActiveIndex !== prevActiveIndexRef.current) {
                    prevActiveIndexRef.current = newActiveIndex;
                    setActiveIndex(newActiveIndex);
                    updateIndicator(newActiveIndex);
                }
            });
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, [visibleLinks, sectionsWithIndex, updateIndicator]);

    useEffect(() => {
        const targetIndex = hoveredIndex !== null ? hoveredIndex : activeIndex;
        if (targetIndex !== null) {
            updateIndicator(targetIndex);
        }
    }, [hoveredIndex, activeIndex, updateIndicator]);

    const scrollToSection = useCallback((hash: string, index: number) => {
        const id = hash.replace(/^#/, '');
        const element = document.getElementById(id);
        if (element && menuRef.current) {
            const menuHeight = menuRef.current.offsetHeight || MenuConstants.DEFAULT_MENU_HEIGHT;
            const offset = MenuConstants.calculateTotalOffset(menuHeight);
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            window.history.pushState(window.history.state, '', hash.startsWith('#') ? hash : `#${id}`);
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
            setActiveIndex(index);
            updateIndicator(index);
            setOpenDropdownIndex(null);
        }
    }, [updateIndicator]);

    if (visibleLinks.length === 0) {
        return null;
    }

    return (
        <nav 
            className="relative z-50 w-full min-w-0 rounded-xl bg-transparent px-2 py-3 shadow-md backdrop-blur-sm sm:py-4" 
            aria-label={MenuConstants.MENU_ARIA_LABEL || "Product detail navigation"}
        >
            <motion.div
                initial="hidden" 
                animate="visible" 
                variants={pageVariants}
                className={`${MenuConstants.SCROLL_CONTAINER_CLASS} relative`}
                ref={menuRef}
            >
                {/* Alt bar: scroll container'a göre sabit, içerik kaydığında hareket etmez */}
                <motion.div
                    className="absolute bottom-0 left-0 h-0.5 bg-alsero-first rounded-full pointer-events-none z-10"
                    style={{
                        x: xSpring,
                        width: widthSpring,
                    }}
                    transition={{ 
                        type: "spring", 
                        stiffness: MenuConstants.SPRING_STIFFNESS, 
                        damping: MenuConstants.SPRING_DAMPING 
                    }}
                />
                <ul 
                    className="flex flex-row flex-nowrap justify-start gap-2 relative min-w-max"
                    role="list"
                >
                {visibleLinks.map((subItem, subIndex) => {
                    const isActive = activeIndex === subIndex;
                    const isHovered = hoveredIndex === subIndex;
                    const isDropdown = subItem.isDropdown && subItem.children && subItem.children.length > 0;
                    const isDropdownOpen = openDropdownIndex === subIndex;
                    const shouldHighlight = isActive || isHovered || (isDropdown && isDropdownOpen);
                    const baseTextClass = "text-gray-700 font-semibold hover:text-alsero-first [&_svg]:text-gray-600 [&_svg]:hover:fill-alsero-first transition-colors duration-200";
                    const activeTextClass = "text-alsero-first font-bold [&_svg]:fill-alsero-first";

                    return (
                        <li 
                            key={subIndex}
                            ref={(el) => { itemRefs.current[subIndex] = el; }}
                            className="group rounded-xl cursor-pointer transition-colors duration-200 relative flex-shrink-0"
                            onMouseEnter={() => {
                                setHoveredIndex(subIndex);
                                updateIndicator(subIndex);
                            }}
                            onMouseLeave={() => {
                                setHoveredIndex(null);
                                if (activeIndex !== null) {
                                    updateIndicator(activeIndex);
                                }
                            }}
                        >
                            {isDropdown ? (
                                <Menu>
                                    {({ open }) => (
                                            <>
                                                <SyncDropdownOpen open={open} subIndex={subIndex} onSync={setOpenDropdownIndex} />
                                                <MenuButton
                                                    className={`flex flex-row gap-2 items-center text-left text-lg font-semibold w-full py-4 px-3 rounded-xl ${shouldHighlight ? activeTextClass : baseTextClass}`}
                                                >
                                                    {subItem.svg ? <span className="flex-shrink-0 [&_svg]:w-6 [&_svg]:h-6" aria-hidden="true" dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(subItem.svg) }} /> : null}
                                                    <span>{subItem.title}</span>
                                                    <svg className={`w-5 h-5 flex-shrink-0 ml-auto transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                    </svg>
                                                </MenuButton>
                                                <MenuItems
                                                    anchor="bottom start"
                                                    className="min-w-[200px] bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-[100] max-h-[70vh] overflow-y-auto outline-none focus:outline-none mt-1"
                                                    aria-label={subItem.title}
                                                >
                                                    {subItem.children!.map((child, childIndex) => {
                                                        const childHash = child.link.startsWith('#') ? child.link : `#${child.link}`;
                                                        return (
                                                            <MenuItem key={childIndex}>
                                                                {({ focus }) => (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => scrollToSection(childHash, subIndex)}
                                                                        className={`w-full text-left px-4 py-2.5 text-sm font-semibold transition-colors rounded-lg ${focus ? 'bg-alsero-first/5 text-alsero-first' : 'text-gray-800 hover:bg-alsero-first/5 hover:text-alsero-first'}`}
                                                                    >
                                                                        {child.title}
                                                                    </button>
                                                                )}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </MenuItems>
                                            </>
                                    )}
                                </Menu>
                            ) : subItem.actionType === 'open_reservation_modal' && onOpenReservationModal ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        onOpenReservationModal();
                                        setOpenDropdownIndex(null);
                                    }}
                                    className={`block w-full flex flex-row gap-2 items-center text-lg font-semibold py-4 px-3 rounded-xl ${shouldHighlight ? activeTextClass : baseTextClass}`}
                                >
                                    {subItem.svg ? <span className="flex-shrink-0 [&_svg]:w-6 [&_svg]:h-6" aria-hidden="true" dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(subItem.svg) }} /> : null}
                                    <span>{subItem.title}</span>
                                    {subItem.tooltip ? (
                                        <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-[120%] opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap rounded-md bg-alsero-dark-gray px-2 py-1 text-xs font-semibold text-white">
                                            {subItem.tooltip}
                                        </span>
                                    ) : null}
                                </button>
                            ) : (
                                <Link 
                                    href={subItem.link}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const targetId = subItem.link.replace(/^#/, '');
                                        const element = document.getElementById(targetId);
                                        if (element) {
                                            const menuHeight = menuRef.current?.offsetHeight || MenuConstants.DEFAULT_MENU_HEIGHT;
                                            const offset = MenuConstants.calculateTotalOffset(menuHeight);
                                            const elementPosition = element.getBoundingClientRect().top;
                                            const offsetPosition = elementPosition + window.pageYOffset - offset;
                                            window.history.pushState(window.history.state, '', subItem.link);
                                            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                                            setActiveIndex(subIndex);
                                            updateIndicator(subIndex);
                                        }
                                    }}
                                    className={`block w-full flex flex-row gap-2 items-center text-lg font-semibold py-4 px-3 rounded-xl ${shouldHighlight ? activeTextClass : baseTextClass}`}
                                    aria-current={isActive ? 'page' : undefined}
                                >
                                    {subItem.svg ? <span className="flex-shrink-0 [&_svg]:w-6 [&_svg]:h-6" aria-hidden="true" dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(subItem.svg) }} /> : null}
                                    <span>{subItem.title}</span>
                                    {subItem.tooltip ? (
                                        <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-[120%] opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap rounded-md bg-alsero-dark-gray px-2 py-1 text-xs font-semibold text-white">
                                            {subItem.tooltip}
                                        </span>
                                    ) : null}
                                </Link>
                            )}
                        </li>
                    );
                })}
                </ul>
            </motion.div>
        </nav>
    )
}